import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowsOut,
  ImageBroken,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  X,
} from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { IconButton, Spinner } from '@/ui'
import { useDialogBehaviour } from '@/ui/useDialogBehaviour'
import { useMediaUrl } from '../api/reference'

export interface PhotoViewerProps {
  /** Media id (phone UUIDv7) or a URL. */
  photo: string | null | undefined
  alt: string
  /** Thumbnail size; `inline` fills its container (side-by-side review). */
  size?: 'sm' | 'md' | 'inline'
  className?: string
  /** Called when the photo is opened large (e.g. an approval that needs the photo looked at). */
  onOpen?: () => void
}

/**
 * Photo thumbnail that opens a zoomable lightbox. A media id that has not arrived from the phone
 * yet shows "not uploaded yet" (PHOTO_MISSING territory) instead of a broken image.
 */
export function PhotoViewer({ photo, alt, size = 'md', className, onOpen }: PhotoViewerProps) {
  const { url, loading, missing, refresh } = useMediaUrl(photo)
  const [open, setOpen] = useState(false)
  // A signed link can run out while the page is open: ask for a fresh one once, then give up.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const [retried, setRetried] = useState(false)
  const onImageError = () => {
    if (!url) return
    setFailedUrl(url)
    if (!retried) {
      setRetried(true)
      refresh()
    }
  }
  const broken = Boolean(url) && failedUrl === url
  const box = cn(
    'relative flex items-center justify-center overflow-hidden rounded-card border border-rule bg-paper-sunk text-muted',
    size === 'sm' && 'h-16 w-16',
    size === 'md' && 'h-32 w-40',
    size === 'inline' && 'min-h-[260px] w-full',
    className,
  )
  if (!photo) {
    return (
      <div className={box} aria-label={`${alt}: no photo`}>
        <span className="px-2 text-center text-[12px]">No photo</span>
      </div>
    )
  }
  if (loading) {
    return (
      <div className={box}>
        <Spinner label="Loading photo" />
      </div>
    )
  }
  if (missing || !url) {
    return (
      <div className={box} title={photo}>
        <span className="flex flex-col items-center gap-1 px-2 text-center text-[12px]">
          <ImageBroken size={22} aria-hidden="true" />
          Not uploaded yet
        </span>
      </div>
    )
  }
  if (broken) {
    return (
      <div className={box} title={alt}>
        <span className="flex flex-col items-center gap-1 px-2 text-center text-[12px]">
          <ImageBroken size={22} aria-hidden="true" />
          Photo could not be loaded
        </span>
      </div>
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true)
          onOpen?.()
        }}
        className={cn(box, 'group cursor-zoom-in focus-visible:border-amber')}
        aria-label={`Open photo: ${alt}`}
      >
        <img
          src={url}
          alt={alt}
          className={cn('h-full w-full', size === 'inline' ? 'object-contain' : 'object-cover')}
          loading="lazy"
          onError={onImageError}
        />
        <span className="absolute bottom-1 right-1 rounded-chip bg-ink/70 p-1 text-paper opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <ArrowsOut aria-hidden="true" />
        </span>
      </button>
      {open && <Lightbox url={url} alt={alt} onClose={() => setOpen(false)} />}
    </>
  )
}

function Lightbox({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  useDialogBehaviour(true, onClose, ref)
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      tabIndex={-1}
      className="fixed inset-0 z-[70] flex flex-col bg-ink/90 focus:outline-none"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-paper">
        <span className="truncate text-[14px]">{alt}</span>
        <div className="flex items-center gap-1">
          <IconButton
            label="Zoom out"
            icon={<MagnifyingGlassMinus />}
            className="text-paper hover:bg-paper/10"
            onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
          />
          <span className="w-12 text-center font-mono text-[12px]">{Math.round(zoom * 100)}%</span>
          <IconButton
            label="Zoom in"
            icon={<MagnifyingGlassPlus />}
            className="text-paper hover:bg-paper/10"
            onClick={() => setZoom((z) => Math.min(5, z + 0.5))}
          />
          <IconButton
            label="Close"
            icon={<X />}
            className="text-paper hover:bg-paper/10"
            onClick={onClose}
          />
        </div>
      </div>
      <div
        className="min-h-0 flex-1 overflow-auto"
        onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2.5))}
      >
        <img
          src={url}
          alt={alt}
          style={{ width: `${zoom * 100}%`, maxWidth: zoom === 1 ? '100%' : 'none' }}
          className={cn('mx-auto block h-auto', zoom === 1 && 'max-h-full object-contain')}
        />
      </div>
    </div>,
    document.body,
  )
}
